/**
 * ResidentDocumentsTab.jsx
 *
 * - Upload resident documents using signed URLs
 * - User selects document category from UI
 * - Optional expiration date (notification-ready)
 * - Metadata saved via GraphQL
 * - Supports delete (file + DB row)
 */

import React, { useContext, useRef, useState } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { Typography } from "@mui/material";
import { uploadToCloudinary } from "../../../../../platform/util/upload/uploadToCloudinary";
import DocumentsTab from "../../../../../platform/components/business/documents/DocumentsTab";
import { UserContext } from "../../../../../platform/components/auth/auth-context";
import UpgradePlan from "../../../../../platform/util/upgrade/upgradePlan";
import { useSelectedBusiness } from "../../../../../platform/redux/store";
import {
    FeatureAccess,
    getFeatureAccess,
} from "../../../../../platform/util/featureAccess";

/* =========================
   Constants
========================= */
const DOCUMENT_TYPES = [
    { value: "CARE_PLAN", label: "Care Plan" },
    { value: "CLIENT_DIAGNOSES", label: "Client Diagnoses" },
    { value: "ASSESSMENT", label: "Assessment" },
    { value: "CONSENT", label: "Consent Form" },
    { value: "LAB", label: "Lab Report" },
    { value: "INSURANCE", label: "Insurance" },
    { value: "OTHER", label: "Other" }
];
const DOCUMENT_TYPE_LABELS = DOCUMENT_TYPES.reduce((acc, type) => {
    acc[type.value] = type.label;
    return acc;
}, {});

/* =========================
   GraphQL
========================= */
const GET_DOCUMENTS = gql`
  query GetResidentDocuments($residentId: ID!) {
    getResidentDocuments(residentId: $residentId) {
      id
      fileName
      fileUrl
      type
      createdAt
      expiresAt
    }
  }
`;

const GENERATE_UPLOAD_URL = gql`
  mutation GenerateUploadUrl($filename: String!, $contentType: String!) {
    generateUploadUrl(filename: $filename, contentType: $contentType)
  }
`;

const ADD_DOCUMENT = gql`
  mutation AddResidentDocument(
    $residentId: ID!
    $type: String!
    $fileUrl: String!
    $fileName: String
    $size: Int
    $staffId: ID
    $expiresAt: DateTime
  ) {
    addResidentDocument(
      residentId: $residentId
      type: $type
      fileUrl: $fileUrl
      fileName: $fileName
      size: $size
      staffId: $staffId
      expiresAt: $expiresAt
    ) {
      id
    }
  }
`;

const DELETE_DOCUMENT = gql`
  mutation DeleteResidentDocument($id: ID!) {
    deleteResidentDocument(id: $id)
  }
`;

/* =========================
   Component
========================= */
export default function ResidentDocumentsTab({ residentId, staffId, isDisabled = false }) {
    const { providerUser } = useContext(UserContext);
    const selectedBusiness = useSelectedBusiness();
    const fileInputRef = useRef(null);

    const [uploading, setUploading] = useState(false);
    const [docType, setDocType] = useState("OTHER");
    const [customTypeName, setCustomTypeName] = useState("");
    const [expiresAt, setExpiresAt] = useState("");
    const [checkPlanNeeded, setCheckPlanNeeded] = useState(false);
    const documentAccess = getFeatureAccess(providerUser, FeatureAccess.DOCUMENT_MANAGEMENT);
    const standardPlanRequired = documentAccess.requiredPlanId;
    const hasStandardDocAccess = documentAccess.hasAccess;
    const currentLoginStaff = selectedBusiness?.businessStaffs?.find(
        (s) => Number(s?.id) === Number(providerUser?.staffId)
    );
    const isAdmin = String(currentLoginStaff?.role?.name || "").trim().toUpperCase() === "ADMIN";
    const docsReadOnly = Boolean(isDisabled || !hasStandardDocAccess || !isAdmin);

    const { data, refetch } = useQuery(GET_DOCUMENTS, {
        variables: { residentId },
        skip: !residentId || !isAdmin
    });

    const [generateUploadUrl] = useMutation(GENERATE_UPLOAD_URL);
    const [addDocument] = useMutation(ADD_DOCUMENT);
    const [deleteDocument] = useMutation(DELETE_DOCUMENT);

    const documents = data?.getResidentDocuments || [];
    /* =========================
       Upload Handlers
    ========================= */
    const handleSelectFile = () => {
        if (docsReadOnly) {
            if (!hasStandardDocAccess) setCheckPlanNeeded(true);
            return;
        }
        fileInputRef.current?.click();
    };

    const handleFileSelected = async (e) => {
        if (docsReadOnly) {
            if (!hasStandardDocAccess) setCheckPlanNeeded(true);
            return;
        }
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);

        try {
            let fileUrl;

            // 🟢 Development → Cloudinary
            if (process.env.REACT_APP_ENV === "development") {
                fileUrl = await uploadToCloudinary(file);
            }
            // 🔵 Production → GCS Signed URL
            else {
                const filename = `resident-documents/${residentId}/${Date.now()}-${file.name}`;

                const { data } = await generateUploadUrl({
                    variables: {
                        filename,
                        contentType: file.type
                    }
                });

                const signedUrl = data.generateUploadUrl;

                await fetch(signedUrl, {
                    method: "PUT",
                    headers: { "Content-Type": file.type },
                    body: file
                });

                fileUrl = signedUrl.split("?")[0];
            }

            // Save metadata
            await addDocument({
                variables: {
                    residentId,
                    type: docType === "OTHER" && customTypeName
                        ? customTypeName
                        : docType,
                    fileUrl,
                    fileName: file.name,
                    size: file.size,
                    staffId,
                    expiresAt: expiresAt || null
                }
            });

            await refetch();
        } catch (err) {
            console.error("Upload failed:", err);
            alert("Failed to upload document");
        } finally {
            setUploading(false);
            setExpiresAt("");
            setCustomTypeName("");
            e.target.value = "";
        }
    };

    /* =========================
       Delete Handler
    ========================= */
    const handleDelete = async (docId) => {
        if (docsReadOnly) {
            if (!hasStandardDocAccess) setCheckPlanNeeded(true);
            return;
        }
        if (!window.confirm("Are you sure you want to delete this document?")) return;

        try {
            await deleteDocument({ variables: { id: docId } });
            await refetch();
        } catch (err) {
            console.error("Delete failed:", err);
            alert("Failed to delete document");
        }
    };

    /* =========================
       Render
    ========================= */
    return (
        <>
            {checkPlanNeeded && (
                <UpgradePlan
                    featureName="Resident document upload and updates"
                    requiredPlanId={standardPlanRequired}
                    onClose={() => setCheckPlanNeeded(false)}
                />
            )}
            {!isAdmin && (
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                    Admin role required to view resident documents.
                </Typography>
            )}
            {isAdmin && !hasStandardDocAccess && (
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                    Standard plan required to upload or delete resident documents.
                </Typography>
            )}
            {isAdmin && (
                <DocumentsTab
                title="Documents"
                documentTypes={DOCUMENT_TYPES}
                documents={documents}
                docType={docType}
                onChangeDocType={setDocType}
                customTypeName={customTypeName}
                onChangeCustomTypeName={setCustomTypeName}
                expiresAt={expiresAt}
                onChangeExpiresAt={setExpiresAt}
                uploading={uploading}
                onSelectFile={handleSelectFile}
                onFileSelected={handleFileSelected}
                onDelete={handleDelete}
                fileInputRef={fileInputRef}
                groupByType
                typeLabelMap={DOCUMENT_TYPE_LABELS}
                isDisabled={docsReadOnly}
            />
            )}
        </>
    );
}
